import React, { useState, useEffect } from "react";
import { getMonedas, crearMoneda, actualizarMoneda, eliminarMoneda, getCotizaciones, crearCotizacion, eliminarCotizacion, Moneda as MonedaType, Cotizacion } from "../../services/api";

const FUENTES = ["Banco Central del Uruguay", "BCRA", "Manual"];
const RELACIONES = ["", "/", "x"];

interface Props {
  empresaId: number;
  token: string;
  puedeModificar: boolean;
}

type ModoPanel = "editar" | "nueva" | "cotizaciones" | null;

export default function Monedas({ empresaId, token, puedeModificar }: Props) {
  const [monedas, setMonedas] = useState<MonedaType[]>([]);
  const [cargando, setCargando] = useState(true);
  const [monedaSeleccionada, setMonedaSeleccionada] = useState<MonedaType | null>(null);
  const [monedaEditando, setMonedaEditando] = useState<MonedaType | null>(null);
  const [modoPanel, setModoPanel] = useState<ModoPanel>(null);
  const [solapa, setSolapa] = useState<"basico" | "descarga">("basico");
  const [guardando, setGuardando] = useState(false);
  const [mensajeOk, setMensajeOk] = useState("");
  const [mensajeError, setMensajeError] = useState("");
  const [confirmando, setConfirmando] = useState(false);
  const [cotizaciones, setCotizaciones] = useState<Cotizacion[]>([]);
  const [nuevaCotizacion, setNuevaCotizacion] = useState({ fecha: "", cotizacion_: 0 });
  const [cargandoCot, setCargandoCot] = useState(false);

  useEffect(() => {
    cargar();
  }, [empresaId]);

  const cargar = async () => {
    const data = await getMonedas(empresaId, token);
    setMonedas(data);
    setCargando(false);
  };

  const monedaVacia = (): MonedaType => ({
    monedaId: 0,
    empresaId,
    nombre: "",
    codigo: "",
    simbolo: null,
    decimales: 2,
    relacion: "/",
    monRelac: 1,
    dcGanGrav: null,
    dcGanNoGrav: null,
    dcPerdGrav: null,
    dcPerdNoGrav: null,
    fuenteDescarga: null,
    monedaFuente: null,
    compVent: null,
    diaAnter: null,
  });

  const handleNueva = () => {
    setMonedaEditando(monedaVacia());
    setModoPanel("nueva");
    setSolapa("basico");
    setMensajeOk("");
    setMensajeError("");
  };

  const handleEditar = (moneda: MonedaType) => {
    setMonedaSeleccionada(moneda);
    setMonedaEditando({ ...moneda });
    setModoPanel("editar");
    setSolapa("basico");
    setMensajeOk("");
    setMensajeError("");
    setConfirmando(false);
  };

  const handleVerCotizaciones = async (moneda: MonedaType) => {
    setMonedaSeleccionada(moneda);
    setModoPanel("cotizaciones");
    setMensajeOk("");
    setMensajeError("");
    setCargandoCot(true);
    const data = await getCotizaciones(empresaId, moneda.codigo, token);
    setCotizaciones(data);
    setCargandoCot(false);
    setNuevaCotizacion({ fecha: new Date().toISOString().split("T")[0], cotizacion_: 0 });
  };

  const cerrarPanel = () => {
    setModoPanel(null);
    setMonedaEditando(null);
    setMonedaSeleccionada(null);
    setMensajeOk("");
    setMensajeError("");
    setConfirmando(false);
  };

  const handleGuardar = async () => {
    if (!monedaEditando) return;
    setGuardando(true);
    setMensajeOk("");
    setMensajeError("");

    if (modoPanel === "nueva") {
      const resultado = await crearMoneda(monedaEditando, token);
      if (resultado.ok) {
        await cargar();
        setMensajeOk("Moneda creada correctamente");
        setTimeout(() => cerrarPanel(), 1500);
      } else {
        setMensajeError(resultado.mensaje);
      }
    } else if (modoPanel === "editar") {
      const ok = await actualizarMoneda(monedaEditando.monedaId, monedaEditando, token);
      if (ok) {
        await cargar();
        setMensajeOk("Guardado correctamente");
      } else {
        setMensajeError("Error al guardar");
      }
    }
    setGuardando(false);
  };

  const handleEliminar = async () => {
    if (!monedaSeleccionada) return;
    setGuardando(true);
    const resultado = await eliminarMoneda(monedaSeleccionada.monedaId, token);
    if (resultado.ok) {
      await cargar();
      cerrarPanel();
    } else {
      setMensajeError(resultado.mensaje);
      setConfirmando(false);
    }
    setGuardando(false);
  };

  const handleAgregarCotizacion = async () => {
    if (!monedaSeleccionada || !nuevaCotizacion.fecha || !nuevaCotizacion.cotizacion_) return;
    setGuardando(true);
    const resultado = await crearCotizacion({
      cotizacionId: 0,
      empresaId,
      codigo: monedaSeleccionada.codigo,
      fecha: nuevaCotizacion.fecha,
      cotizacion_: nuevaCotizacion.cotizacion_,
    }, token);
    if (resultado.ok) {
      const data = await getCotizaciones(empresaId, monedaSeleccionada.codigo, token);
      setCotizaciones(data);
      setNuevaCotizacion({ fecha: new Date().toISOString().split("T")[0], cotizacion_: 0 });
    } else {
      setMensajeError(resultado.mensaje);
    }
    setGuardando(false);
  };

  const handleEliminarCotizacion = async (id: number) => {
    await eliminarCotizacion(id, token);
    const data = await getCotizaciones(empresaId, monedaSeleccionada!.codigo, token);
    setCotizaciones(data);
  };

  if (cargando) return <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Cargando monedas...</div>;

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
      {/* TABLA */}
      <div style={{ ...styles.container, flex: 1 }}>
        <div style={styles.toolbar}>
          <span style={styles.titulo}>Monedas</span>
          {puedeModificar && (
            <button style={styles.btnNuevo} onClick={handleNueva}>+ Nueva moneda</button>
          )}
        </div>
        <div style={styles.encabezado}>
          <div style={{ ...styles.colH, width: 80 }}>Código</div>
          <div style={{ ...styles.colH, width: 60 }}>Símbolo</div>
          <div style={{ ...styles.colH, flex: 1 }}>Nombre</div>
          <div style={{ ...styles.colH, width: 80, textAlign: "center" }}>Dec.</div>
          <div style={{ ...styles.colH, width: 100, textAlign: "center" }}>Relación</div>
          <div style={{ ...styles.colH, width: 120, textAlign: "center" }}>Acciones</div>
        </div>
        <div style={styles.tabla}>
          {monedas.length === 0 && (
            <div style={styles.vacio}>No hay monedas cargadas</div>
          )}
          {monedas.map(m => (
            <div
              key={m.monedaId}
              style={{
                ...styles.fila,
                backgroundColor: monedaSeleccionada?.monedaId === m.monedaId ? "#e8f4fc" : "#fff",
              }}
            >
              <div style={{ ...styles.col, width: 80, fontFamily: "monospace", fontWeight: 700, color: "#2B8FCC" }}>{m.codigo}</div>
              <div style={{ ...styles.col, width: 60, color: "#888" }}>{m.simbolo || ""}</div>
              <div style={{ ...styles.col, flex: 1 }}>{m.nombre}</div>
              <div style={{ ...styles.col, width: 80, justifyContent: "center" }}>{m.decimales}</div>
              <div style={{ ...styles.col, width: 100, justifyContent: "center", color: "#888" }}>
                {m.monRelac === 1 ? "Principal" : m.monRelac === 2 ? "Secundaria" : "—"} {m.relacion || ""}
              </div>
              <div style={{ ...styles.col, width: 120, justifyContent: "center", gap: 8 }}>
                {puedeModificar && (
                  <button style={styles.btnAccion} onClick={() => handleEditar(m)}>Editar</button>
                )}
                <button style={{ ...styles.btnAccion, backgroundColor: "#f0f8ff", color: "#2B8FCC" }}
                  onClick={() => handleVerCotizaciones(m)}>
                  Cotiz.
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* PANEL EDITAR/NUEVA */}
      {(modoPanel === "editar" || modoPanel === "nueva") && monedaEditando && (
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <span style={styles.panelTitulo}>{modoPanel === "nueva" ? "Nueva Moneda" : "Editar Moneda"}</span>
            <button style={styles.panelCerrar} onClick={cerrarPanel}>✕</button>
          </div>

          <div style={styles.solapas}>
            {(["basico", "descarga"] as const).map(s => (
              <button key={s} style={{ ...styles.solapa, ...(solapa === s ? styles.solapaActiva : {}) }}
                onClick={() => setSolapa(s)}>
                {s === "basico" ? "Básico" : "Descarga"}
              </button>
            ))}
          </div>

          {solapa === "basico" && (
            <div>
              <div style={styles.campo}>
                <label style={styles.campoLabel}>Código</label>
                <input style={styles.campoInput} value={monedaEditando.codigo}
                  onChange={e => setMonedaEditando({ ...monedaEditando, codigo: e.target.value.toUpperCase() })} />
              </div>
              <div style={styles.campo}>
                <label style={styles.campoLabel}>Nombre</label>
                <input style={styles.campoInput} value={monedaEditando.nombre}
                  onChange={e => setMonedaEditando({ ...monedaEditando, nombre: e.target.value })} />
              </div>
              <div style={styles.campo}>
                <label style={styles.campoLabel}>Símbolo</label>
                <input style={styles.campoInput} value={monedaEditando.simbolo || ""}
                  onChange={e => setMonedaEditando({ ...monedaEditando, simbolo: e.target.value })} />
              </div>
              <div style={styles.campo}>
                <label style={styles.campoLabel}>Decimales</label>
                <input style={styles.campoInput} type="number" min={0} max={6}
                  value={monedaEditando.decimales}
                  onChange={e => setMonedaEditando({ ...monedaEditando, decimales: Number(e.target.value) })} />
              </div>
              <div style={styles.campo}>
                <label style={styles.campoLabel}>Relacionada con</label>
                <select style={styles.campoInput} value={monedaEditando.monRelac ?? 1}
                  onChange={e => setMonedaEditando({ ...monedaEditando, monRelac: Number(e.target.value) })}>
                  <option value={1}>Principal</option>
                  <option value={2}>Secundaria</option>
                </select>
              </div>
              <div style={styles.campo}>
                <label style={styles.campoLabel}>Operación</label>
                <select style={styles.campoInput} value={monedaEditando.relacion || "/"}
                  onChange={e => setMonedaEditando({ ...monedaEditando, relacion: e.target.value })}>
                  <option value="/">/  (Dividir)</option>
                  <option value="x">x  (Multiplicar)</option>
                </select>
              </div>
            </div>
          )}

          {solapa === "descarga" && (
            <div>
              <div style={styles.campo}>
                <label style={styles.campoLabel}>Fuente</label>
                <select style={styles.campoInput} value={monedaEditando.fuenteDescarga || ""}
                  onChange={e => setMonedaEditando({ ...monedaEditando, fuenteDescarga: e.target.value || null })}>
                  <option value="">— Sin descarga automática —</option>
                  {FUENTES.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              {monedaEditando.fuenteDescarga && monedaEditando.fuenteDescarga !== "Manual" && (
                <>
                  <div style={styles.campo}>
                    <label style={styles.campoLabel}>Moneda en la fuente</label>
                    <input style={styles.campoInput} value={monedaEditando.monedaFuente || ""}
                      onChange={e => setMonedaEditando({ ...monedaEditando, monedaFuente: e.target.value })} />
                  </div>
                  <div style={styles.campo}>
                    <label style={styles.campoLabel}>Compra / Venta</label>
                    <select style={styles.campoInput} value={monedaEditando.compVent || "C"}
                      onChange={e => setMonedaEditando({ ...monedaEditando, compVent: e.target.value })}>
                      <option value="C">Compra</option>
                      <option value="V">Venta</option>
                    </select>
                  </div>
                  <div style={styles.campo}>
                    <label style={styles.campoLabel}>Día anterior / mismo día</label>
                    <select style={styles.campoInput} value={monedaEditando.diaAnter ?? 1}
                      onChange={e => setMonedaEditando({ ...monedaEditando, diaAnter: Number(e.target.value) })}>
                      <option value={1}>Día hábil anterior</option>
                      <option value={0}>Mismo día</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          )}

          {mensajeOk && <div style={styles.mensajeOk}>✓ {mensajeOk}</div>}
          {mensajeError && <div style={styles.mensajeError}>✕ {mensajeError}</div>}

          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button style={styles.btnGuardar} onClick={handleGuardar} disabled={guardando}>
              {guardando ? "Guardando..." : "Aceptar"}
            </button>
            <button style={styles.btnCancelar} onClick={cerrarPanel}>Cancelar</button>
          </div>

          {modoPanel === "editar" && !confirmando && (
            <button style={styles.btnEliminar} onClick={() => setConfirmando(true)}>Eliminar moneda</button>
          )}
          {confirmando && (
            <div style={styles.confirmBox}>
              <p style={styles.confirmTexto}>¿Eliminar la moneda <strong>{monedaSeleccionada?.codigo}</strong>?</p>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={{ ...styles.btnGuardar, backgroundColor: "#e74c3c" }} onClick={handleEliminar} disabled={guardando}>
                  {guardando ? "Eliminando..." : "Eliminar"}
                </button>
                <button style={styles.btnCancelar} onClick={() => setConfirmando(false)}>Cancelar</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* PANEL COTIZACIONES */}
      {modoPanel === "cotizaciones" && monedaSeleccionada && (
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <span style={styles.panelTitulo}>Cotizaciones — {monedaSeleccionada.codigo}</span>
            <button style={styles.panelCerrar} onClick={cerrarPanel}>✕</button>
          </div>

          {puedeModificar && (
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <input style={{ ...styles.campoInput, flex: 1 }} type="date"
                value={nuevaCotizacion.fecha}
                onChange={e => setNuevaCotizacion({ ...nuevaCotizacion, fecha: e.target.value })} />
              <input style={{ ...styles.campoInput, width: 100 }} type="number" step="0.0001"
                value={nuevaCotizacion.cotizacion_}
                onChange={e => setNuevaCotizacion({ ...nuevaCotizacion, cotizacion_: Number(e.target.value) })} />
              <button style={styles.btnNuevo} onClick={handleAgregarCotizacion} disabled={guardando}>+</button>
            </div>
          )}

          {mensajeError && <div style={styles.mensajeError}>✕ {mensajeError}</div>}

          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            {cargandoCot && <div style={{ textAlign: "center", color: "#888", padding: 20 }}>Cargando...</div>}
            {!cargandoCot && cotizaciones.length === 0 && (
              <div style={styles.vacio}>Sin cotizaciones cargadas</div>
            )}
            {cotizaciones.map(c => (
              <div key={c.cotizacionId} style={{ display: "flex", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f5f5f5", gap: 8 }}>
                <span style={{ flex: 1, fontSize: 13, color: "#666" }}>{new Date(c.fecha).toLocaleDateString("es-UY")}</span>
                <span style={{ width: 100, textAlign: "right", fontWeight: 600, fontSize: 14 }}>
                  {c.cotizacion_.toFixed(monedaSeleccionada.decimales)}
                </span>
                {puedeModificar && (
                  <button style={{ ...styles.btnAccion, color: "#e74c3c", border: "1px solid #e74c3c" }}
                    onClick={() => handleEliminarCotizacion(c.cotizacionId)}>✕</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: { backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden" },
  toolbar: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid #f0f0f0" },
  titulo: { fontSize: 15, fontWeight: 700, color: "#1a1a2e" },
  btnNuevo: { padding: "8px 16px", backgroundColor: "#2B8FCC", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  encabezado: { display: "flex", alignItems: "center", padding: "8px 16px", backgroundColor: "#f4f6f8", borderBottom: "2px solid #e0e0e0" },
  colH: { fontSize: 11, fontWeight: 700, color: "#999", letterSpacing: "0.5px", textTransform: "uppercase" as const, padding: "0 8px" },
  tabla: { overflowY: "auto" as const, maxHeight: "calc(100vh - 260px)" },
  fila: { display: "flex", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid #f5f5f5", fontSize: 13 },
  col: { display: "flex", alignItems: "center", padding: "0 8px" },
  vacio: { padding: 40, textAlign: "center" as const, color: "#bbb", fontSize: 14 },
  btnAccion: { padding: "4px 10px", backgroundColor: "#f4f6f8", color: "#555", border: "1px solid #ddd", borderRadius: 6, fontSize: 12, cursor: "pointer" },
  panel: { width: 320, backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.1)", padding: 20, flexShrink: 0 },
  panelHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  panelTitulo: { fontSize: 14, fontWeight: 700, color: "#1a1a2e" },
  panelCerrar: { background: "transparent", border: "none", cursor: "pointer", fontSize: 16, color: "#aaa" },
  solapas: { display: "flex", borderBottom: "2px solid #e0e0e0", marginBottom: 16 },
  solapa: { padding: "7px 14px", border: "none", background: "transparent", fontSize: 13, cursor: "pointer", color: "#888", borderBottom: "2px solid transparent", marginBottom: -2 },
  solapaActiva: { color: "#2B8FCC", fontWeight: 600, borderBottom: "2px solid #2B8FCC" },
  campo: { marginBottom: 12 },
  campoLabel: { display: "block", fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase" as const, letterSpacing: "0.5px", marginBottom: 4 },
  campoInput: { width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", fontSize: 13, boxSizing: "border-box" as const, outline: "none" },
  btnGuardar: { flex: 1, padding: 10, backgroundColor: "#2B8FCC", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnCancelar: { flex: 1, padding: 10, backgroundColor: "#f4f6f8", color: "#555", border: "1px solid #ddd", borderRadius: 8, fontSize: 13, cursor: "pointer" },
  btnEliminar: { width: "100%", padding: 10, backgroundColor: "transparent", color: "#e74c3c", border: "1px solid #e74c3c", borderRadius: 8, fontSize: 13, cursor: "pointer", marginTop: 8 },
  mensajeOk: { backgroundColor: "#e8f8f0", color: "#27ae60", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginTop: 8, textAlign: "center" as const },
  mensajeError: { backgroundColor: "#fff0f0", color: "#e74c3c", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginTop: 8, textAlign: "center" as const },
  confirmBox: { backgroundColor: "#fff8e8", border: "1px solid #f0d080", borderRadius: 8, padding: 14, marginTop: 12 },
  confirmTexto: { fontSize: 13, color: "#333", marginBottom: 12 },
};